import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { Campaign, CampaignTaskType } from '../../../../types';
import {
  domainOptionsFor,
  requiresDomainSplit,
  supportsDomainSplit,
  type DomainOption,
} from './domainOptions';
import {
  TaskTypeDescriptor,
  type TaskTypeDescriptorCapabilities,
  listTaskTypeDescriptors,
  getTaskTypeDescriptor,
} from '../../../../taskTypes/descriptor';
import { CampaignProgressBar } from '../../../../components/CampaignProgressBar';
import { SearchableDropdown } from '../../../../components/SearchableDropdown';
import { UploadTasksModal } from './UploadTasksModal';
import { CampaignCounts, campaignTypeCountsKey } from './viewModel';
import { CampaignTaskDomainInput } from '../../../../services/adminCampaigns';
import { useTaskTypeRuntime } from '../../../../taskTypes/useTaskTypeRuntime';
import type { AdjustCampaignTaskPoolResult } from '../../../../domain/campaigns/admin';

export type TaskTypeEditorMode = 'pools' | 'settings';

interface TaskTypeEditorProps {
  campaign: Campaign;
  /** Render pool management or the per-task-type settings controls. */
  mode?: TaskTypeEditorMode;
  /** Limit the editor to these bound task types. Used by the settings tabs. */
  taskTypeIds?: string[];
  countsByType: Map<string, CampaignCounts>;
  countsLoading: boolean;
  pending: boolean;
  showProgressBar?: boolean;
  onApply: (taskTypeId: string, targetCount: number, domainId?: string | null, domainLabel?: string | null) => Promise<unknown>;
  onPreview: (taskTypeId: string, targetCount: number, domainId?: string | null, domainLabel?: string | null) => Promise<AdjustCampaignTaskPoolResult>;
  onSetDomains: (taskTypeId: string, domains: CampaignTaskDomainInput[]) => Promise<void>;
  onToggleLlmReview: (taskTypeId: string, enabled: boolean) => Promise<void>;
  onToggleLlmValidator: (taskTypeId: string, enabled: boolean) => Promise<void>;
  onSetClaimingPaused: (taskTypeId: string, paused: boolean, domainId?: string | null) => Promise<void>;
  onReloadCounts: () => Promise<void>;
}

export function TaskTypeEditor({
  campaign,
  mode = 'pools',
  taskTypeIds,
  countsByType,
  countsLoading,
  pending,
  showProgressBar = true,
  onApply,
  onPreview,
  onSetDomains,
  onToggleLlmReview,
  onToggleLlmValidator,
  onSetClaimingPaused,
  onReloadCounts,
}: TaskTypeEditorProps) {
  const modules = listTaskTypeDescriptors();
  const [adding, setAdding] = useState(false);
  const existingIds = new Set(campaign.taskTypes.map((t) => t.taskTypeId));
  const visibleTaskTypes = taskTypeIds
    ? campaign.taskTypes.filter((entry) => taskTypeIds.includes(entry.taskTypeId))
    : campaign.taskTypes;
  const addableModules = mode === 'pools'
    ? modules.filter((m) => m.id !== 'legacy' && !existingIds.has(m.id))
    : [];

  return (
    <div className={mode === 'settings' ? '' : 'rounded-md border border-gray-200 bg-white p-4'}>
      {visibleTaskTypes.length === 0 && !adding && (
        <p className="text-sm text-gray-500">
          No task types bound to this campaign yet. Add one below to start creating tasks.
        </p>
      )}

      <div className="space-y-3">
        {visibleTaskTypes.map((entry) => (
          <TaskTypeRow
            key={entry.taskTypeId}
            campaignId={campaign.id}
            campaignName={campaign.name || '(no name)'}
            entry={entry}
            mode={mode}
            counts={countsByType.get(campaignTypeCountsKey(campaign.id, entry.taskTypeId)) ?? null}
            countsByType={countsByType}
            countsLoading={countsLoading}
            pending={pending}
            showProgressBar={showProgressBar}
            onApply={onApply}
            onPreview={onPreview}
            onSetDomains={onSetDomains}
            onToggleLlmReview={onToggleLlmReview}
            onToggleLlmValidator={onToggleLlmValidator}
            onSetClaimingPaused={onSetClaimingPaused}
            onReloadCounts={onReloadCounts}
          />
        ))}
        {mode === 'pools' && adding && addableModules.length > 0 && (
          <AddTaskTypeRow
            availableModules={addableModules}
            pending={pending}
            onCancel={() => setAdding(false)}
            onApply={onApply}
            onApplied={() => setAdding(false)}
          />
        )}
      </div>

      {mode === 'pools' && !adding && addableModules.length > 0 && (
        <button
          type="button"
          onClick={() => setAdding(true)}
          disabled={pending}
          className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-primary hover:text-primary-hover disabled:opacity-50"
        >
          <Plus className="h-4 w-4" />
          Add task type
        </button>
      )}
      {mode === 'pools' && !adding && addableModules.length === 0 && campaign.taskTypes.length > 0 && (
        <p className="mt-3 text-xs text-gray-400">
          All registered task types are already bound to this campaign.
        </p>
      )}
    </div>
  );
}

interface TaskTypeRowProps {
  campaignId: string;
  campaignName: string;
  entry: CampaignTaskType;
  mode: TaskTypeEditorMode;
  counts: CampaignCounts | null;
  countsByType: Map<string, CampaignCounts>;
  countsLoading: boolean;
  pending: boolean;
  showProgressBar: boolean;
  onApply: (taskTypeId: string, targetCount: number, domainId?: string | null, domainLabel?: string | null) => Promise<unknown>;
  onPreview: (taskTypeId: string, targetCount: number, domainId?: string | null, domainLabel?: string | null) => Promise<AdjustCampaignTaskPoolResult>;
  onSetDomains: (taskTypeId: string, domains: CampaignTaskDomainInput[]) => Promise<void>;
  onToggleLlmReview: (taskTypeId: string, enabled: boolean) => Promise<void>;
  onToggleLlmValidator: (taskTypeId: string, enabled: boolean) => Promise<void>;
  onSetClaimingPaused: (taskTypeId: string, paused: boolean, domainId?: string | null) => Promise<void>;
  onReloadCounts: () => Promise<void>;
}

function TaskTypeRow({
  campaignId,
  campaignName,
  entry,
  mode,
  counts,
  countsByType,
  countsLoading,
  pending,
  showProgressBar,
  onApply,
  onPreview,
  onSetDomains,
  onToggleLlmReview,
  onToggleLlmValidator,
  onSetClaimingPaused,
  onReloadCounts,
}: TaskTypeRowProps) {
  const mod = getTaskTypeDescriptor(entry.taskTypeId);
  const uploadRequired = mod.capabilities.payloadUpload;
  const supportsDomains = supportsDomainSplit(entry.taskTypeId);
  const domainSplitRequired = requiresDomainSplit(entry.taskTypeId);
  const [notice, setNotice] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    setNotice(null);
    setErr(null);
  }, [entry.taskTypeId]);

  const canRemove = (counts?.total ?? 0) - (counts?.available ?? 0) === 0 && (counts?.total ?? 0) > 0;
  const activeCount = Math.max(0, (counts?.total ?? 0) - (counts?.available ?? 0));
  const visibleDomainStats = (entry.domains ?? [])
    .map((domain) => ({
      domain,
      domainCounts: countsByType.get(campaignTypeCountsKey(campaignId, entry.taskTypeId, domain.domainId)) ?? null,
    }))
    .filter(({ domainCounts }) => countsLoading || (domainCounts?.total ?? 0) > 0);

  const applyTarget = async (nextCount: number) => {
    setErr(null);
    setNotice(null);
    const result: any = await onApply(entry.taskTypeId, nextCount);
    const assigned = typeof result.assigned === 'number' && result.assigned > 0
      ? ` Assigned ${result.assigned}.`
      : '';
    setNotice(
      `Applied. Created ${result.created}, deleted ${result.deleted}.${assigned} Now ${result.availableAfter} available / ${result.totalAfter} total.`,
    );
    return result;
  };

  const removeBinding = async () => {
    setErr(null);
    setNotice(null);
    try {
      await applyTarget(0);
    } catch (e: any) {
      setErr(e?.message || 'Failed to remove task type.');
    }
  };

  return (
    <div className={mode === 'settings' ? '' : 'rounded border border-gray-200 bg-gray-50 p-3'}>
      {mode === 'pools' && (
        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-gray-900">{mod.shortName}</p>
            <p className="text-xs text-gray-500">
              {mod.displayName} ({entry.taskTypeId})
            </p>
          </div>
          <button
            type="button"
            onClick={() => void removeBinding()}
            disabled={!canRemove || pending}
            title={canRemove ? 'Remove this task type' : 'Cannot remove while tasks of this type exist'}
            className="inline-flex h-9 items-center justify-center rounded-md border border-gray-300 bg-white px-2 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-40"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      )}

      {mode === 'settings' && (
        <TaskTypeSettingsSection
          campaignId={campaignId}
          campaignName={campaignName}
          entry={entry}
          mod={mod}
          uploadRequired={uploadRequired}
          supportsDomains={supportsDomains}
          domainSplitRequired={domainSplitRequired}
          domainsActive={(entry.domains?.length ?? 0) > 0}
          disabled={pending}
          activeCount={activeCount}
          onApply={onApply}
          onPreview={onPreview}
          onSetDomains={onSetDomains}
          onToggleLlmReview={onToggleLlmReview}
          onToggleLlmValidator={onToggleLlmValidator}
          onSetClaimingPaused={onSetClaimingPaused}
          onReloadCounts={onReloadCounts}
        />
      )}

      {mode === 'pools' && showProgressBar && (
        <CampaignProgressBar counts={counts} loading={countsLoading} />
      )}

      {mode === 'pools' && (
        <div className="mt-2 text-xs text-gray-600">
          {countsLoading ? (
            <span className="text-gray-400">Loading task counts…</span>
          ) : counts ? (
            <span>
              {uploadRequired
                ? `${counts.available} available · ${counts.total} total in pool`
                : `${counts.available} available / ${entry.targetCount} target · ${counts.total} total in pool`}
            </span>
          ) : (
            <span className="text-gray-400">No counts loaded.</span>
          )}
        </div>
      )}

      {mode === 'pools' && visibleDomainStats.length > 0 && (
        <div className="mt-3 space-y-2 rounded-md border border-gray-200 bg-white p-2">
          {visibleDomainStats.map(({ domain, domainCounts }) => {
            return (
              <div key={domain.domainId}>
                <div className="flex items-center justify-between gap-2 text-xs">
                  <span className="font-medium text-gray-800">{domain.label}</span>
                  <span className="text-gray-500">
                    {countsLoading || !domainCounts
                      ? 'Loading…'
                      : `${domainCounts.available} available / ${domain.targetCount} target · ${domainCounts.total} total`}
                  </span>
                </div>
                <CampaignProgressBar counts={domainCounts} loading={countsLoading} />
              </div>
            );
          })}
        </div>
      )}

      {notice && <p className="mt-2 text-xs text-emerald-700">{notice}</p>}
      {err && <p className="mt-2 text-xs text-red-600">{err}</p>}

    </div>
  );
}

function UploadTasksRuntimeModal({
  campaignId,
  campaignName,
  taskTypeId,
  onUploaded,
}: {
  campaignId: string;
  campaignName: string;
  taskTypeId: string;
  onUploaded: () => void;
}) {
  const runtime = useTaskTypeRuntime(taskTypeId);
  if (runtime.status === 'loading') {
    return (
      <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-4 text-sm text-gray-600">
        <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
        Loading upload parser…
      </div>
    );
  }
  if (runtime.status === 'error' || !runtime.module) {
    return (
      <div className="rounded-md border border-red-200 bg-red-50 px-3 py-3 text-sm text-red-800">
        <p className="font-medium">The upload parser could not be loaded.</p>
        {runtime.error && <p className="mt-1 text-xs">{runtime.error.message}</p>}
        <div className="mt-3">
          <button
            type="button"
            onClick={runtime.retry}
            className="rounded border border-red-300 bg-white px-3 py-1.5 text-sm font-medium text-red-700"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }
  return (
    <UploadTasksModal
      campaignId={campaignId}
      campaignName={campaignName}
      taskType={runtime.module}
      inline={true}
      onClose={() => undefined}
      onUploaded={onUploaded}
    />
  );
}

interface TaskTypeSettingsSectionProps {
  campaignId: string;
  campaignName: string;
  entry: CampaignTaskType;
  mod: TaskTypeDescriptor;
  uploadRequired: boolean;
  supportsDomains: boolean;
  domainSplitRequired: boolean;
  domainsActive: boolean;
  disabled: boolean;
  activeCount: number;
  onApply: (taskTypeId: string, targetCount: number, domainId?: string | null, domainLabel?: string | null) => Promise<unknown>;
  onPreview: (taskTypeId: string, targetCount: number, domainId?: string | null, domainLabel?: string | null) => Promise<AdjustCampaignTaskPoolResult>;
  onSetDomains: (taskTypeId: string, domains: CampaignTaskDomainInput[]) => Promise<void>;
  onToggleLlmReview: (taskTypeId: string, enabled: boolean) => Promise<void>;
  onToggleLlmValidator: (taskTypeId: string, enabled: boolean) => Promise<void>;
  onSetClaimingPaused: (taskTypeId: string, paused: boolean, domainId?: string | null) => Promise<void>;
  onReloadCounts: () => Promise<void>;
}

/**
 * Inline editor for every per-(campaign, taskType) setting. All of the forms
 * stay visible in the settings section; saving no longer requires opening a
 * menu or dialog.
 */
function TaskTypeSettingsSection({
  campaignId,
  campaignName,
  entry,
  mod,
  uploadRequired,
  supportsDomains,
  domainSplitRequired,
  domainsActive,
  disabled,
  activeCount,
  onApply,
  onPreview,
  onSetDomains,
  onToggleLlmReview,
  onToggleLlmValidator,
  onSetClaimingPaused,
  onReloadCounts,
}: TaskTypeSettingsSectionProps) {
  return (
    <div className="space-y-3">
      {uploadRequired && (
        <UploadTasksRuntimeModal
          campaignId={campaignId}
          campaignName={campaignName}
          taskTypeId={entry.taskTypeId}
          onUploaded={() => {
            void onReloadCounts();
          }}
        />
      )}
      {!uploadRequired && !domainSplitRequired && !domainsActive && (
        <EditTargetCountModal
          mod={mod}
          campaignName={campaignName}
          entry={entry}
          activeCount={activeCount}
          onApply={async (next) => {
            await onApply(entry.taskTypeId, next);
          }}
          onPreview={async (next) => onPreview(entry.taskTypeId, next)}
        />
      )}
      {supportsDomains && (
        <EditDomainsModal
          mod={mod}
          campaignName={campaignName}
          entry={entry}
          pending={disabled}
          onApply={async (domainId, label, targetCount) => {
            await onApply(entry.taskTypeId, targetCount, domainId, label);
          }}
          onSetDomains={async (domains) => {
            await onSetDomains(entry.taskTypeId, domains);
          }}
        />
      )}
      <EditClaimingPausedModal
        mod={mod}
        campaignName={campaignName}
        entry={entry}
        onSave={async (paused, domainId) => {
          await onSetClaimingPaused(entry.taskTypeId, paused, domainId);
        }}
      />
      {mod.capabilities.llmReviewer && (
        <EditLlmReviewModal
          mod={mod}
          campaignName={campaignName}
          entry={entry}
          onSave={async (enabled) => {
            await onToggleLlmReview(entry.taskTypeId, enabled);
          }}
        />
      )}
      {mod.capabilities.llmValidator && (
        <EditLlmValidatorModal
          mod={mod}
          campaignName={campaignName}
          entry={entry}
          onSave={async (enabled) => {
            await onToggleLlmValidator(entry.taskTypeId, enabled);
          }}
        />
      )}
    </div>
  );
}

interface ModalShellProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  labelId: string;
}

function ModalShell({ title, subtitle, children, footer, labelId }: ModalShellProps) {
  return (
    <div
      className="border-b border-gray-200 py-4 last:border-b-0 first:pt-1 last:pb-1"
      role="group"
      aria-labelledby={labelId}
    >
      <div className="flex items-start justify-between gap-3 pb-2">
        <div className="min-w-0">
          <h3 id={labelId} className="text-sm font-semibold text-gray-900">
            {title}
          </h3>
          {subtitle && <p className="mt-0.5 truncate text-xs text-gray-500">{subtitle}</p>}
        </div>
      </div>
      <div className="space-y-3 pt-3 text-sm text-gray-700">{children}</div>
      {footer && <div className="mt-3 flex items-center justify-end gap-2">{footer}</div>}
    </div>
  );
}

interface EditTargetCountModalProps {
  mod: TaskTypeDescriptor;
  campaignName: string;
  entry: CampaignTaskType;
  activeCount: number;
  onApply: (targetCount: number) => Promise<void>;
  onPreview: (targetCount: number) => Promise<AdjustCampaignTaskPoolResult>;
}

interface EditDomainsModalProps {
  mod: TaskTypeDescriptor;
  campaignName: string;
  entry: CampaignTaskType;
  pending: boolean;
  onApply: (domainId: string, label: string, targetCount: number) => Promise<void>;
  onSetDomains: (domains: CampaignTaskDomainInput[]) => Promise<void>;
}

function EditDomainsModal({
  mod,
  campaignName,
  entry,
  pending: parentPending,
  onApply,
  onSetDomains,
}: EditDomainsModalProps) {
  const existing = new Map((entry.domains ?? []).map((d) => [d.domainId, d]));
  const domainOptions = useMemo(
    () => domainOptionsFor(entry.taskTypeId) ?? [],
    [entry.taskTypeId],
  );
  const [rows, setRows] = useState(() =>
    domainOptions.map((domain) => ({
      ...domain,
      targetCount: existing.get(domain.domainId)?.targetCount ?? 0,
      claimingPaused: existing.get(domain.domainId)?.claimingPaused === true,
    })),
  );
  const [pending, setPending] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const busy = pending || parentPending;
  const valid = rows.every((row) => Number.isInteger(row.targetCount) && row.targetCount >= 0 && row.targetCount <= 1000);
  const dirty = rows.some((row) => (existing.get(row.domainId)?.targetCount ?? 0) !== row.targetCount);

  useEffect(() => {
    const nextExisting = new Map((entry.domains ?? []).map((d) => [d.domainId, d]));
    setRows(
      domainOptions.map((domain) => ({
        ...domain,
        targetCount: nextExisting.get(domain.domainId)?.targetCount ?? 0,
        claimingPaused: nextExisting.get(domain.domainId)?.claimingPaused === true,
      })),
    );
    setErr(null);
  }, [entry.taskTypeId, entry.domains, domainOptions]);

  const submit = async () => {
    if (!valid || (!dirty && busy) || busy) return;
    setPending(true);
    setErr(null);
    try {
      const activeRows = rows.filter((row) => row.targetCount > 0);
      if (!dirty) {
        await onSetDomains(activeRows);
      } else {
        for (const row of rows) {
          if ((existing.get(row.domainId)?.targetCount ?? 0) !== row.targetCount) {
            await onApply(row.domainId, row.label, row.targetCount);
          }
        }
      }
      setPending(false);
    } catch (e: any) {
      setErr(e?.message || 'Failed to apply domains.');
      setPending(false);
    }
  };

  return (
    <ModalShell
      title="Domains"
      subtitle={`${campaignName} · ${mod.shortName}`}
      labelId="edit-domains-title"
      footer={
        <>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={!valid || busy}
            className="inline-flex items-center justify-center rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-hover disabled:opacity-50"
          >
            {busy ? (
              <>
                <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                Applying…
              </>
            ) : (
              'Apply'
            )}
          </button>
        </>
      }
    >
      <p className="text-xs text-gray-500">
        Set the task target for each domain. Each non-zero domain becomes its own claim queue and stats row.
      </p>
      <div className="space-y-2">
        {rows.map((row, index) => (
          <label key={row.domainId} className="flex items-center justify-between gap-3">
            <span className="text-sm font-medium text-gray-800">{row.label}</span>
            <input
              type="number"
              min={0}
              max={1000}
              value={row.targetCount}
              onChange={(e) => {
                const next = Math.max(0, Math.min(1000, Number.parseInt(e.target.value, 10) || 0));
                setRows((prev) => prev.map((item, i) => (i === index ? { ...item, targetCount: next } : item)));
              }}
              disabled={busy}
              className="w-28 rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:bg-gray-50"
            />
          </label>
        ))}
      </div>
      {err && <p className="text-xs text-red-600">{err}</p>}
    </ModalShell>
  );
}

function EditTargetCountModal({
  mod,
  campaignName,
  entry,
  activeCount,
  onApply,
  onPreview,
}: EditTargetCountModalProps) {
  const [value, setValue] = useState<number>(entry.targetCount);
  const [pending, setPending] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState<AdjustCampaignTaskPoolResult | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const valid = Number.isInteger(value) && value >= 0 && value <= 1000;
  const dirty = value !== entry.targetCount;

  useEffect(() => {
    setValue(entry.targetCount);
    setPreview(null);
    setErr(null);
  }, [entry.taskTypeId, entry.targetCount]);

  const previewPlan = async () => {
    if (!valid || !dirty || pending || previewing) return;
    setPreviewing(true);
    setErr(null);
    try {
      setPreview(await onPreview(value));
    } catch (e: any) {
      setErr(e?.message || 'Failed to preview.');
    } finally {
      setPreviewing(false);
    }
  };

  const submit = async () => {
    if (!valid || !dirty || pending) return;
    setPending(true);
    setErr(null);
    try {
      await onApply(value);
      setPending(false);
    } catch (e: any) {
      setErr(e?.message || 'Failed to apply.');
      setPending(false);
    }
  };

  return (
    <ModalShell
      title="Target count"
      subtitle={`${campaignName} · ${mod.shortName}`}
      labelId="edit-target-count-title"
      footer={
        <>
          <button
            type="button"
            onClick={() => void previewPlan()}
            disabled={!valid || !dirty || pending || previewing}
            className="inline-flex items-center justify-center rounded-md border border-primary bg-white px-3 py-1.5 text-sm font-medium text-primary hover:bg-primary/5 disabled:opacity-50"
          >
            {previewing ? 'Previewing…' : 'Preview'}
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={!valid || !dirty || pending}
            className="inline-flex items-center justify-center rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-hover disabled:opacity-50"
          >
            {pending ? (
              <>
                <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                Applying…
              </>
            ) : (
              'Apply'
            )}
          </button>
        </>
      }
    >
      <p className="text-xs text-gray-500">
        Total tasks of this type to keep in the pool. Raising creates new
        tasks; lowering deletes available tasks (newest first).
      </p>
      <label className="block">
        <span className="mb-1 block text-xs font-medium text-gray-700">Target count</span>
        <input
          type="number"
          min={0}
          max={1000}
          value={value}
          onChange={(e) => setValue(Number.parseInt(e.target.value, 10) || 0)}
          disabled={pending}
          className="w-32 rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:bg-gray-50"
        />
      </label>
      {activeCount > 0 && (
        <p className="text-xs text-gray-500">
          {activeCount} task{activeCount === 1 ? ' is' : 's are'} already claimed or
          in review and won't be deleted.
        </p>
      )}
      {preview && (
        <p className="rounded-md border border-blue-100 bg-blue-50 px-2 py-1.5 text-xs text-blue-800">
          Preview: creates {preview.created}, deletes {preview.deleted}
          {preview.assigned ? `, assigns ${preview.assigned}` : ''};
          {' '}pool would be {preview.availableAfter} available / {preview.totalAfter} total.
        </p>
      )}
      {err && <p className="text-xs text-red-600">{err}</p>}
    </ModalShell>
  );
}

interface EditClaimingPausedModalProps {
  mod: TaskTypeDescriptor;
  campaignName: string;
  entry: CampaignTaskType;
  onSave: (paused: boolean, domainId?: string | null) => Promise<void>;
}

function EditClaimingPausedModal({
  mod,
  campaignName,
  entry,
  onSave,
}: EditClaimingPausedModalProps) {
  const hasDomains = (entry.domains?.length ?? 0) > 0;
  const [entryPaused, setEntryPaused] = useState<boolean>(entry.claimingPaused === true);
  const [domainPaused, setDomainPaused] = useState<Record<string, boolean>>(() =>
    Object.fromEntries((entry.domains ?? []).map((domain) => [domain.domainId, domain.claimingPaused === true])),
  );
  const [pending, setPending] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    setEntryPaused(entry.claimingPaused === true);
    setDomainPaused(
      Object.fromEntries((entry.domains ?? []).map((domain) => [domain.domainId, domain.claimingPaused === true])),
    );
    setErr(null);
  }, [entry.taskTypeId, entry.claimingPaused, entry.domains]);

  const save = async (next: boolean, domainId: string | null) => {
    if (pending) return;
    setPending(true);
    setErr(null);
    try {
      await onSave(next, domainId);
    } catch (e: any) {
      setErr(e?.message || 'Failed to save.');
    } finally {
      setPending(false);
    }
  };

  return (
    <ModalShell
      title="Claiming"
      subtitle={`${campaignName} · ${mod.shortName}`}
      labelId="edit-claiming-paused-title"
    >
      <p className="text-xs text-gray-500">
        Pausing stops experts from claiming new tasks in the selected queue. Existing claimed tasks and reviews are unchanged.
      </p>
      {hasDomains ? (
        <div className="space-y-2">
          {(entry.domains ?? []).map((domain) => (
            <label key={domain.domainId} className="flex items-center justify-between gap-3 rounded-md border border-gray-200 px-3 py-2">
              <span className="text-sm font-medium text-gray-800">{domain.label}</span>
              <span className="flex items-center gap-2 text-xs text-gray-600">
                Paused
                <input
                  type="checkbox"
                  checked={domainPaused[domain.domainId] === true}
                  onChange={(e) => {
                    const next = e.target.checked;
                    setDomainPaused((prev) => ({ ...prev, [domain.domainId]: next }));
                    void save(next, domain.domainId);
                  }}
                  disabled={pending}
                  className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary disabled:opacity-50"
                />
              </span>
            </label>
          ))}
        </div>
      ) : (
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={entryPaused}
            onChange={(e) => {
              const next = e.target.checked;
              setEntryPaused(next);
              void save(next, null);
            }}
            disabled={pending}
            className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary disabled:opacity-50"
          />
          Pause claiming for this queue
        </label>
      )}
      {err && <p className="text-xs text-red-600">{err}</p>}
    </ModalShell>
  );
}

/**
 * Read-only description of what this task type's validator actually does,
 * read from the generated capability catalog.
 *
 * `aiDetectionBlocksSubmit` is the reason this exists: it is the only
 * capability that can refuse an expert's submission, and until catalog v2 it
 * was invisible outside a single line in a server validator file. It is shown
 * rather than edited — whether detection gates submit is a property of the
 * task type, not of the campaign binding (see decision D8).
 */
function ValidatorCapabilitySummary({
  validator,
}: {
  validator: TaskTypeDescriptorCapabilities['validator'];
}) {
  if (!validator) return null;
  const passes = [
    validator.llm ? 'a language-model review of the draft' : null,
    validator.aiDetection ? 'an AI-authorship check on the prose fields' : null,
  ].filter(Boolean) as string[];

  return (
    <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600">
      <p>
        <span className="font-medium text-gray-700">This validator runs:</span>
        {' '}
        {passes.join(' and ')}
        .
      </p>
      <p className="mt-1">
        <span className="font-medium text-gray-700">Blocks submit:</span>
        {' '}
        {validator.aiDetectionBlocksSubmit
          ? 'yes — a first submit whose prose scores AI-authored is refused, '
            + 'with the findings shown. Pressing Submit again goes through.'
          : 'no — every comment is advisory.'}
      </p>
      {validator.aiDetectionBlocksSubmit && (
        <p className="mt-1 text-gray-500">
          This is a property of the task type and applies whether or not
          pre-submit validation is enabled for this campaign.
        </p>
      )}
    </div>
  );
}

interface EditLlmValidatorModalProps {
  mod: TaskTypeDescriptor;
  campaignName: string;
  entry: CampaignTaskType;
  onSave: (enabled: boolean) => Promise<void>;
}

function EditLlmValidatorModal({
  mod,
  campaignName,
  entry,
  onSave,
}: EditLlmValidatorModalProps) {
  const initial = entry.llmValidator?.enabled === true;
  const [enabled, setEnabled] = useState<boolean>(initial);
  const [pending, setPending] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    setEnabled(initial);
    setErr(null);
  }, [entry.taskTypeId, initial]);

  const save = async (next: boolean) => {
    if (pending) return;
    setPending(true);
    setErr(null);
    try {
      await onSave(next);
    } catch (e: any) {
      setErr(e?.message || 'Failed to save.');
    } finally {
      setPending(false);
    }
  };

  return (
    <ModalShell
      title="Pre-submit validation"
      subtitle={`${campaignName} · ${mod.shortName}`}
      labelId="edit-llm-validator-title"
    >
      <p className="text-xs text-gray-500">
        Adds a Validate button next to Submit in the work UI. Experts click it
        to run this task type&rsquo;s pre-flight checks; results render inline
        below each field as advisory comments. Validation never writes to the
        task — comments are transient and visible only to the expert.
      </p>
      <ValidatorCapabilitySummary validator={mod.capabilities.validator} />
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => {
            const next = e.target.checked;
            setEnabled(next);
            void save(next);
          }}
          disabled={pending}
          className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary disabled:opacity-50"
        />
        Enable pre-submit validation for this task type
      </label>
      {err && <p className="text-xs text-red-600">{err}</p>}
    </ModalShell>
  );
}

interface EditLlmReviewModalProps {
  mod: TaskTypeDescriptor;
  campaignName: string;
  entry: CampaignTaskType;
  onSave: (enabled: boolean) => Promise<void>;
}

function EditLlmReviewModal({
  mod,
  campaignName,
  entry,
  onSave,
}: EditLlmReviewModalProps) {
  const initial = entry.llmReview?.enabled === true;
  const [enabled, setEnabled] = useState<boolean>(initial);
  const [pending, setPending] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    setEnabled(initial);
    setErr(null);
  }, [entry.taskTypeId, initial]);

  const save = async (next: boolean) => {
    if (pending) return;
    setPending(true);
    setErr(null);
    try {
      await onSave(next);
    } catch (e: any) {
      setErr(e?.message || 'Failed to save.');
    } finally {
      setPending(false);
    }
  };

  return (
    <ModalShell
      title="AI pre-review"
      subtitle={`${campaignName} · ${mod.shortName}`}
      labelId="edit-llm-review-title"
    >
      <p className="text-xs text-gray-500">
        Runs an AI reviewer when a task enters review; the human reviewer gets
        pending comments to approve, edit, or delete.
      </p>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => {
            const next = e.target.checked;
            setEnabled(next);
            void save(next);
          }}
          disabled={pending}
          className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary disabled:opacity-50"
        />
        Enable AI pre-review for this task type
      </label>
      {err && <p className="text-xs text-red-600">{err}</p>}
    </ModalShell>
  );
}

interface AddTaskTypeRowProps {
  availableModules: TaskTypeDescriptor[];
  pending: boolean;
  onCancel: () => void;
  onApply: (taskTypeId: string, targetCount: number, domainId?: string | null, domainLabel?: string | null) => Promise<unknown>;
  onApplied: () => void;
}

function AddTaskTypeRow({ availableModules, pending, onCancel, onApply, onApplied }: AddTaskTypeRowProps) {
  const [taskTypeId, setTaskTypeId] = useState<string>(availableModules[0]?.id ?? '');
  const [targetCount, setTargetCount] = useState<number>(0);
  const [domainRows, setDomainRows] = useState<Array<DomainOption & { targetCount: number }>>(
    () => (domainOptionsFor(taskTypeId) ?? []).map((domain) => ({ ...domain, targetCount: 0 })),
  );
  const [err, setErr] = useState<string | null>(null);

  const selected = availableModules.find((m) => m.id === taskTypeId);
  const uploadRequired = selected?.capabilities.payloadUpload === true;
  const domainSplit = supportsDomainSplit(taskTypeId);

  // Domain split is required for the workflow and drug-discovery types
  // (backend rejects a positive targetCount without a domainId) and optional
  // for gdm-rubrics.
  // When optional, "no domains entered" means create a plain top-level pool.
  const domainSplitRequired = requiresDomainSplit(taskTypeId);
  const domainTotal = domainRows.reduce((sum, row) => sum + row.targetCount, 0);
  const useDomains = domainSplit && (domainSplitRequired || domainTotal > 0);

  useEffect(() => {
    setDomainRows(
      (domainOptionsFor(taskTypeId) ?? []).map((domain) => ({ ...domain, targetCount: 0 })),
    );
  }, [taskTypeId]);

  const effectiveTarget = uploadRequired || useDomains ? 0 : targetCount;
  const domainRowsValid = domainRows.every((row) => (
    Number.isInteger(row.targetCount) && row.targetCount >= 0 && row.targetCount <= 1000
  ));
  const valid = useDomains
    ? taskTypeId.length > 0 && domainRowsValid && domainTotal > 0 && domainTotal <= 1000
    : domainSplitRequired
      ? false
      : taskTypeId.length > 0 && Number.isInteger(effectiveTarget) && effectiveTarget >= 0;

  const apply = async () => {
    setErr(null);
    try {
      if (useDomains) {
        for (const row of domainRows) {
          if (row.targetCount > 0) {
            await onApply(taskTypeId, row.targetCount, row.domainId, row.label);
          }
        }
      } else {
        await onApply(taskTypeId, effectiveTarget);
      }
      onApplied();
    } catch (e: any) {
      setErr(e?.message || 'Failed to apply');
    }
  };

  return (
    <div className="rounded border border-primary/40 bg-primary/5 p-3">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_auto_auto_auto] sm:items-end">
        <div>
          <SearchableDropdown
            label="Task type"
            options={availableModules.map((module) => ({
              value: module.id,
              label: `${module.displayName} (${module.id})`,
              searchText: `${module.displayName} ${module.id}`,
            }))}
            value={taskTypeId}
            onChange={(e) => {
              if (e !== null) setTaskTypeId(e);
              setErr(null);
            }}
            disabled={pending}
            searchPlaceholder="Search task types"
            preserveOrder
            triggerClassName="text-sm disabled:bg-gray-50"
          />
        </div>
        {!domainSplitRequired && (
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-700">Target count</label>
          <input
            type="number"
            min={0}
            max={1000}
            value={effectiveTarget}
            onChange={(e) => setTargetCount(Number.parseInt(e.target.value, 10) || 0)}
            disabled={pending || uploadRequired || useDomains}
            title={
              uploadRequired
                ? 'Created via Upload tasks after binding.'
                : useDomains
                  ? 'Disabled while domain targets are non-zero. Clear domain rows to use a flat target.'
                  : undefined
            }
            className="w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:bg-gray-50 sm:w-24"
          />
        </div>
        )}
        <button
          type="button"
          onClick={apply}
          disabled={!valid || pending}
          className="inline-flex items-center justify-center rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-hover disabled:opacity-50"
        >
          {pending ? (
            <>
              <Loader2 className="mr-1 h-4 w-4 animate-spin" />
              Adding…
            </>
          ) : (
            'Add'
          )}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={pending}
          className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50"
        >
          Cancel
        </button>
      </div>
      {domainSplit && (
        <div className="mt-3 rounded-md border border-gray-200 bg-white p-3">
          <div className="mb-2 flex items-center justify-between gap-3">
            <p className="text-xs font-medium text-gray-700">Domain split</p>
            <p className="text-xs text-gray-500">{domainTotal} total</p>
          </div>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {domainRows.map((row, index) => (
              <label key={row.domainId} className="flex items-center justify-between gap-2">
                <span className="text-sm text-gray-800">{row.label}</span>
                <input
                  type="number"
                  min={0}
                  max={1000}
                  value={row.targetCount}
                  onChange={(e) => {
                    const next = Math.max(0, Math.min(1000, Number.parseInt(e.target.value, 10) || 0));
                    setDomainRows((prev) => prev.map((item, i) => (
                      i === index ? { ...item, targetCount: next } : item
                    )));
                  }}
                  disabled={pending}
                  className="w-24 rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:bg-gray-50"
                />
              </label>
            ))}
          </div>
        </div>
      )}
      {uploadRequired && (
        <p className="mt-2 text-xs text-gray-600">
          This task type requires tasks to be uploaded. After adding, click{' '}
          <span className="font-medium">Upload tasks…</span> on the new row.
        </p>
      )}
      {domainSplit && (
        <p className="mt-2 text-xs text-gray-600">
          {domainSplitRequired
            ? 'Create this task type by domain. Future changes are made from Settings → Domains on the row.'
            : 'Optionally segment by domain (set any non-zero target below) or leave all at 0 to use the flat target. Future changes are made from Settings → Domains on the row.'}
        </p>
      )}
      {err && <p className="mt-2 text-xs text-red-600">{err}</p>}
    </div>
  );
}
